import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Stack,
  Switch,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as React from 'react';

import { Card } from '@/imports/ui/chakra/components/Card';

import { Accessibility_CurrentUser } from './graphql-types/Accessibility_CurrentUser';
import {
  Accessibility_EditUserNotificationSoundPreferences,
  Accessibility_EditUserNotificationSoundPreferencesVariables,
} from './graphql-types/Accessibility_EditUserNotificationSoundPreferences';

const CURRENT_USER = gql`
  query Accessibility_CurrentUser {
    currentUser {
      _id
      shouldPlayNotificationSoundOnSend
      shouldPlayNotificationSoundOnReceive
    }
  }
`;

const EDIT_USER_NOTIFICATION_SOUND_PREFERENCES = gql`
  mutation Accessibility_EditUserNotificationSoundPreferences(
    $input: EditUserNotificationSoundPreferencesInput!
  ) {
    editUserNotificationSoundPreferences(input: $input) {
      user {
        _id
        shouldPlayNotificationSoundOnSend
        shouldPlayNotificationSoundOnReceive
      }
    }
  }
`;

export const Accessibility: React.FC = () => {
  const toast = useToast();

  const query = useQuery<Accessibility_CurrentUser>(CURRENT_USER);

  const currentUser = query.data?.currentUser;

  const [
    editUserNotificationSoundPreferences,
    editUserNotificationSoundPreferencesResult,
  ] = useMutation<
    Accessibility_EditUserNotificationSoundPreferences,
    Accessibility_EditUserNotificationSoundPreferencesVariables
  >(EDIT_USER_NOTIFICATION_SOUND_PREFERENCES);

  return (
    <Card overflow="hidden" borderRadius="lg">
      <Stack spacing="0" p="4" fontSize="sm" bg="secondary.500">
      <Text textStyle="xlBodyStrong">Accessibility</Text>
      <Text fontSize="sx" color="#64748B">
        Adjust accessibility settings here for optimal user experience.
      </Text>
      </Stack> 
      <Stack overflow="hidden" borderRadius="0" as={Stack} spacing="0">
        <Text fontSize="sm" p="4" bg="bgPrimary.500" color="textPrimary.500" fontWeight="600">
          <Text>Sound</Text>
        </Text>
        <Stack px="4" py="4">
          <FormControl
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            w="full"
            isDisabled={editUserNotificationSoundPreferencesResult.loading}
          >
            <FormLabel mb="0" color="textPrimary.500" _disabled={{ color: "textPrimary.500", opacity: 1 }}>
              Notification Sound When Receiving Direct Messages
            </FormLabel>
            <Switch
              isChecked={currentUser?.shouldPlayNotificationSoundOnReceive}
              onChange={async ({ target: { checked } }) => {
                try {
                  if (!currentUser) {
                    throw new Error('Unexpected Missing Current User');
                  }

                  await editUserNotificationSoundPreferences({
                    variables: {
                      input: {
                        userId: currentUser._id,
                        shouldPlayNotificationSoundOnReceive: checked,
                      },
                    },
                    optimisticResponse: {
                      editUserNotificationSoundPreferences: {
                        user: {
                          ...currentUser,
                          shouldPlayNotificationSoundOnReceive: checked,
                        },
                        __typename:
                          'EditUserNotificationSoundPreferencesOutput',
                      },
                    },
                  });

                  toast({
                    status: 'success',
                    description: 'Updated preference',
                  });
                } catch (err) {
                  toast({
                    status: 'error',
                    description: 'Failed to update preference.',
                  });
                }
              }}
            />
          </FormControl>
        </Stack>
        <Divider border="1px solid" borderColor="secondary.500" />
        <Stack px="4" py="4">
          <FormControl
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            w="full"
            isDisabled={editUserNotificationSoundPreferencesResult.loading}
          >
            <FormLabel mb="0" color="textPrimary.500" _disabled={{ color: "textPrimary.500", opacity: 1 }}>
              Notification Sound When Sending Direct Messages
            </FormLabel>
            <Switch
              isChecked={currentUser?.shouldPlayNotificationSoundOnSend}
              onChange={async ({ target: { checked } }) => {
                try {
                  if (!currentUser) {
                    throw new Error('Unexpected Missing Current User');
                  }

                  await editUserNotificationSoundPreferences({
                    variables: {
                      input: {
                        userId: currentUser._id,
                        shouldPlayNotificationSoundOnSend: checked,
                      },
                    },
                    optimisticResponse: {
                      editUserNotificationSoundPreferences: {
                        user: {
                          ...currentUser,
                          shouldPlayNotificationSoundOnSend: checked,
                        },
                        __typename:
                          'EditUserNotificationSoundPreferencesOutput',
                      },
                    },
                  });

                  toast({
                    status: 'success',
                    description: 'Updated preference',
                  });
                } catch (err) {
                  toast({
                    status: 'error',
                    description: 'Failed to update preference.',
                  });
                }
              }}
            />
          </FormControl>
        </Stack>
      </Stack>
    </Card>
  );
};
